import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  PlusIcon,
  PopConfirm,
  Show,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FEE_PLAN_LIST } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { handleCapitalize } from "utils/common";
import { extraColumnsField } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";

const FeePlans = () => {
  useDocumentTitle("Fee Plan Setup");

  const navigate = useNavigate();

  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [selectFeePlanId, setSelectFeePlanId] = useState<any>(null);

  const requestBody = useGenerateQueryParams("feePlans");
  const {
    data: getFeePlans,
    isFetching: feePlansFetching,
    refetch,
  } = useFetchQuery({
    key: ["GET_FEE_PLAN_LIST", requestBody],
    route: FEE_PLAN_LIST,
    requestBody,
  });

  const { mutate: deleteFeePlan, isLoading } = useMutation({
    mutationKey: ["DELETE_FEE_PLAN"],
    mutationFn: (id) => axiosInstance.delete(`/fee-plans/${id}/`),
    onSuccess: () => {
      refetch();
      setSelectFeePlanId(null);
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Fee Plan Name", "name", 150, 1),
      valueGetter: (params) => params.row.name || "-",
    },
    {
      ...extraColumnsField("Revenue Products", "revenueProducts", 150, 1),
      valueGetter: (params) =>
        handleCapitalize(
          params.row.revenueProducts
            ?.map((dataItem: any) => dataItem.replace("_", " "))
            .join(", ") || "-",
        ),
    },
    {
      ...extraColumnsField("AUM Mapped", "totalAumMapped", 150, 1),
      valueGetter: (param) => param?.row?.totalAumMapped,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Portfolio Mapped", "totalPortfolio", 150, 1),
      valueGetter: (param) => param?.row?.totalPortfolio,
    },
    {
      ...extraColumnsField("Fee Billed", "totalFeeGenerated", 150, 1),
      valueGetter: (param) => param?.row?.totalFeeGenerated,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Fee Collected", "totalFeeCollected", 150, 1),
      valueGetter: (param) => param?.row?.totalFeeCollected,
      valueFormatter: (params) => moneyCommaSeparator(params.value || 0),
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Grid2 container gap={1} display={"flex"} justifyContent={"flex-end"}>
          <IconButton
            className="outline"
            color="error"
            disabled={params.row.id === selectFeePlanId && isLoading}
            onClick={(e) => {
              e.stopPropagation();
              setSelectFeePlanId(params.id);
              setConfirmationModal(e.currentTarget);
            }}
          >
            <DeleteIcon width={20} />
          </IconButton>
        </Grid2>
      ),
    },
  ];

  return (
    <>
      <GKDataGrid
        loading={feePlansFetching}
        columns={columns}
        rows={getFeePlans?.data?.data || []}
        maxHeight={"calc(100vh - 312px)"}
        name="feePlans"
        headerComponent={
          <GKButton
            variant="contained"
            onClick={() => {
              navigate("/app/fee-plans/create-fee-plan");
            }}
            startIcon={<PlusIcon width={16} />}
          >
            Create Fee Plan
          </GKButton>
        }
        onRowClick={(row) => {
          navigate(`/app/fee-plans/${row.id}/edit-fee-plan`);
        }}
        totalCount={getFeePlans?.data?.totalCount || 0}
      />

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={() => deleteFeePlan(selectFeePlanId)}
        />
      </Show.When>
    </>
  );
};

export default FeePlans;
